// @ts-nocheck
import StoreFormWrapper from "@store/Library/Components/Foundation/StoreForm/StoreFormWrapper";
import InfosBox from "@store/Library/Components/Toolkit/InfosBox";
import TabGroup, { TabGroupPresentationProps } from "@store/MobiusOverrides/TabGroup/TabGroup";
import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { ValidateFields } from "@insite/client-framework/Store/Pages/UserSetup/Handlers/SetUserFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay, { LoadingOverlayProps } from "@insite/mobius/LoadingOverlay";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TextArea, { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkbox from "@store/MobiusOverrides/Checkbox/Checkbox";
import Select, { SelectPresentationProps } from "@store/MobiusOverrides/Select/Select";
import availableCategoriesField from "@store/Framework/Common/Fields/RequestAQuoteForm/availableCategoriesField";
import availableStatesField from "@store/Framework/Common/Fields/RequestAQuoteForm/availableStatesField";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { ChangeEvent, FC, FormEvent, RefObject, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

/* Store Custom Code - Start */
import {
    useIsRequestFormLoading,
    useSendRequestAQuote,
} from "@store/Framework/Store/Bunzl/Components/RequestAQuoteForm/RequestAQuoteFormHooks";
import { RequestAQuoteModel } from "@store/Framework/Store/Bunzl/Components/RequestAQuoteForm/RequestAQuoteFormTypes";
import ReCAPTCHA from "react-google-recaptcha";
/* Store Custom Code - End */
interface StateModel {
    fields: {
        name: string;
        abbreviation: string;
    };
}

interface CategoryModel {
    fields: {
        name: string;
    };
}

const enum fields {
    title1 = "title1",
    text1 = "text1",
    title2 = "title2",
    text2 = "text2",
    title3 = "title3",
    text3 = "text3",
    successMessage = "successMessage",
    availableCategories = "availableCategories",
    availableStates = "availableStates",
    validateReCaptcha = "validateReCaptcha",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title1]: string;
        [fields.text1]: string;
        [fields.title2]: string;
        [fields.text2]: string;
        [fields.title3]: string;
        [fields.text3]: string;
        [fields.successMessage]: string;
        [fields.availableCategories]: CategoryModel[];
        [fields.availableStates]: StateModel[];
        [fields.validateReCaptcha]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

interface RequestAQuoteFormStyles {
    tab?: TabPresentationProps;
    tabGroup?: TabGroupPresentationProps;
    titleTab?: TypographyPresentationProps;
    textTab?: TypographyPresentationProps;
    textField?: TextFieldPresentationProps;
    textArea?: TextAreaPresentationProps;
    selectField?: SelectPresentationProps;
    formFieldsAligner?: InjectableCss;
    formFieldsWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    productFieldsRow?: InjectableCss;
    productFieldColumn?: InjectableCss;
}

const requestAQuoteFormStyles: RequestAQuoteFormStyles = {
    tab: {
        css: css`
            ${({ theme }) => theme.tab.defaultProps?.css}
            width: calc(100% / 3);
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabContent: css`
                background-color: ${getColor("common.accent")};
                min-height: ${pxToRem(520)};
            `,
        },
    },
    titleTab: {
        css: css`
            text-align: center;
        `,
    },
    textTab: {
        css: css`
            display: inherit;
            text-align: center;
        `,
    },
    formFieldsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: ${pxToRem(40, 0)};
        `,
    },
    formFieldsAligner: {
        css: css`
            width: 100%;
            max-width: ${pxToRem(500)};
        `,
    },
    inputWrapper: {
        css: css`
            ${contentSpacingStyles(pxToRem(20), 0, pxToRem(20))}
        `,
    },
    productFieldsRow: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;

            &:not(:last-child) {
                margin: 0 0 20px;
                padding: 0 0 20px;
                border-bottom: 1px solid ${getColor("common.border")};
            }
        `,
    },
    productFieldColumn: {
        css: css`
            width: calc(100% / 3);
            ${contentSpacingStyles(0, pxToRem(20), 0)}
        `,
    },
    textArea: {
        css: css`
            &::placeholder {
                font-size: ${({ theme }) => theme.design.typography.size.tiny};
            }
        `,
    },
};

const styles = requestAQuoteFormStyles;

const StyledForm = getStyledWrapper("form");

const RequestAQuoteForm: FC<Props> = ({ fields, validateReCaptcha }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const sendRequestAQuote = useSendRequestAQuote();
    const isLoading = useIsRequestFormLoading();
    const [formError, setFormError] = useState<string>();
    const [isSuccess, setIsSuccess] = useState(false);
    const dispatch = useDispatch();
    const categories = useAppSelector(state => getCategoriesDataView(state).value);
    const [formFields, setFields] = useState<RequestAQuoteModel>({
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        subscribeToNewsLetter: false,
        companyName: undefined,
        phone: undefined,
        state: undefined,
        products: [
            {
                type: "",
                number: 0,
                comment: "",
            },
            {
                type: "",
                number: 0,
                comment: "",
            },
            {
                type: "",
                number: 0,
                comment: "",
            },
        ],
    });
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    useEffect(() => {
        dispatch(loadCategories());
    }, []);

    const handleFieldChange = <TValue,>(value: TValue, fieldKey: string) => {
        setFields(prevFields => ({
            ...prevFields,
            [fieldKey]: value,
        }));
    };

    const handleProductFieldChange = <TValue,>(value: TValue, key: string, index: number) => {
        const newProductsArray = formFields.products.map((product, i) => {
            if (index === i) {
                return {
                    ...product,
                    [key]: value,
                };
            }

            return product;
        });

        handleFieldChange(newProductsArray, "products");
    };

    const validateFields = (fields: string[]) => {
        const hasSomeFieldUndefined = fields.some(x => {
            return (formFields as any)[x] === undefined || (formFields as any)[x] === "";
        });

        return hasSomeFieldUndefined;
    };

    const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (fields.validateReCaptcha) {
            const isReCaptchaValid = await validateReCaptcha({ location: "RquestAQuote", recaptchaRef });

            if (!isReCaptchaValid) {
                return;
            }
        }

        sendRequestAQuote(
            formFields,
            () => {
                setIsSuccess(true);
            },
            errorMessage => {
                setFormError(errorMessage);
            },
        );
    };

    return (
        <LoadingOverlay loading={isLoading}>
            {isSuccess ? (
                <InfosBox type="success" size="medium">
                    {fields.successMessage}
                </InfosBox>
            ) : (
                <StoreFormWrapper generalErrorMessage={formError}>
                    <StyledForm ref={formRef} onSubmit={onSubmitHandler}>
                        <TabGroup
                            validateRecaptcha={fields.validateReCaptcha}
                            showNextPrevButtons
                            lastNextButtonText={translate("Submit")}
                            lastButtonType="submit"
                            {...styles.tabGroup}
                            canGoNextPage={[
                                validateFields(["firstName", "lastName", "email"]),
                                validateFields(["companyName", "phone", "state"]),
                            ]}
                        >
                            <Tab tabKey="1" headline="STEP 1 - ABOUT YOU" {...styles.tab} disabled>
                                {fields.title1 && (
                                    <Typography variant="h3" as="h5" {...styles.titleTab}>
                                        {fields.title1}
                                    </Typography>
                                )}
                                {fields.text1 && <Typography {...styles.textTab}>{fields.text1}</Typography>}
                                <StyledWrapper {...styles.formFieldsWrapper}>
                                    <StyledWrapper {...styles.formFieldsAligner}>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <TextField
                                                id="firstName"
                                                {...styles.textField}
                                                label={translate("First Name")}
                                                onChange={e => handleFieldChange(e.currentTarget.value, "firstName")}
                                                value={formFields.firstName}
                                                placeholder={translate("John")}
                                                autoComplete="firstName"
                                                required
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <TextField
                                                id="lastName"
                                                {...styles.textField}
                                                label={translate("Last Name")}
                                                onChange={e => handleFieldChange(e.currentTarget.value, "lastName")}
                                                value={formFields.lastName}
                                                placeholder={translate("Doe")}
                                                autoComplete="lastName"
                                                required
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <TextField
                                                id="email"
                                                {...styles.textField}
                                                label={translate("Email")}
                                                onChange={e => handleFieldChange(e.currentTarget.value, "email")}
                                                value={formFields.email}
                                                placeholder={translate("example@example.com")}
                                                autoComplete="email"
                                                required
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <Checkbox
                                                uid="subscribeToNewsLetter"
                                                onChange={(e, value) =>
                                                    handleFieldChange(value, "subscribeToNewsLetter")
                                                }
                                                checked={formFields.subscribeToNewsLetter}
                                            >
                                                {translate(
                                                    "Subscribe to Kishigo emails, promotions, and cool workwear news.",
                                                )}
                                            </Checkbox>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </Tab>
                            <Tab tabKey="2" headline="STEP 2 - BUSINESS INFO" {...styles.tab} disabled>
                                {fields.title2 && (
                                    <Typography variant="h3" as="h5" {...styles.titleTab}>
                                        {fields.title2}
                                    </Typography>
                                )}
                                {fields.text2 && <Typography {...styles.textTab}>{fields.text2}</Typography>}
                                <StyledWrapper {...styles.formFieldsWrapper}>
                                    <StyledWrapper {...styles.formFieldsAligner}>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <TextField
                                                id="companyName"
                                                name="companyName"
                                                {...styles.textField}
                                                label={translate("Company Name")}
                                                onChange={e => handleFieldChange(e.currentTarget.value, "companyName")}
                                                value={formFields.companyName}
                                                placeholder={translate("Example .INC")}
                                                autoComplete="companyName"
                                                required
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <TextField
                                                id="phone"
                                                name="phone"
                                                {...styles.textField}
                                                label={translate("Phone")}
                                                onChange={e => handleFieldChange(e.currentTarget.value, "phone")}
                                                value={formFields.phone}
                                                placeholder={translate("0123456789")}
                                                autoComplete="phone"
                                                required
                                            />
                                        </StyledWrapper>
                                        <StyledWrapper {...styles.inputWrapper}>
                                            <Select
                                                id="state"
                                                name="state"
                                                {...styles.selectField}
                                                label={translate("State")}
                                                onChange={e => handleFieldChange(e.currentTarget.value, "state")}
                                                value={formFields.state}
                                                placeholder={translate("example@example.com")}
                                                autoComplete="state"
                                                required
                                            >
                                                <option value="" selected disabled hidden>
                                                    {translate("Select a state")}
                                                </option>
                                                {fields.availableStates?.map(x => (
                                                    <option key={x.fields.abbreviation} value={x.fields.abbreviation}>
                                                        {x.fields.name}
                                                    </option>
                                                ))}
                                            </Select>
                                        </StyledWrapper>
                                    </StyledWrapper>
                                </StyledWrapper>
                            </Tab>
                            <Tab tabKey="3" headline="STEP 3 - PRODUCTS" {...styles.tab}>
                                {fields.title3 && (
                                    <Typography variant="h3" as="h5" {...styles.titleTab}>
                                        {fields.title3}
                                    </Typography>
                                )}
                                {fields.text3 && <Typography {...styles.textTab}>{fields.text3}</Typography>}
                                <StyledWrapper {...styles.formFieldsWrapper}>
                                    {formFields.products.map((x, index) => {
                                        const key = `${x.type}-${index}`;

                                        return (
                                            <StyledWrapper key={key} {...styles.productFieldsRow}>
                                                <StyledWrapper {...styles.productFieldColumn}>
                                                    <Select
                                                        id="type"
                                                        name="type"
                                                        {...styles.selectField}
                                                        label={translate(`${index + 1}) Type of Product`)}
                                                        onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                                                            handleProductFieldChange(
                                                                e.currentTarget.value,
                                                                "type",
                                                                index,
                                                            )
                                                        }
                                                        value={x.type}
                                                        autoComplete="type"
                                                        required={index === 0}
                                                    >
                                                        <option value="" selected disabled hidden>
                                                            {translate("Select a product type")}
                                                        </option>
                                                        {fields.availableCategories?.map(x => (
                                                            <option key={x.fields.name} value={x.fields.name}>
                                                                {x.fields.name}
                                                            </option>
                                                        ))}
                                                    </Select>
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.productFieldColumn}>
                                                    <TextField
                                                        id="number"
                                                        name="number"
                                                        type="number"
                                                        {...styles.textField}
                                                        label={translate("HOW MANY DO YOU NEED")}
                                                        onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                            handleProductFieldChange(e.target.value, "number", index)
                                                        }
                                                        value={x.number}
                                                        placeholder={translate("Enter Number of Items")}
                                                        autoComplete="number"
                                                        required={index === 0}
                                                    />
                                                </StyledWrapper>
                                                <StyledWrapper {...styles.productFieldColumn}>
                                                    <TextArea
                                                        id="comment"
                                                        name="comment"
                                                        {...styles.textArea}
                                                        label={translate("ANY ADDITIONAL COMMENTS?")}
                                                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                                            handleProductFieldChange(e.target.value, "comment", index)
                                                        }
                                                        value={x.comment}
                                                        placeholder={translate(
                                                            "Anything else we should know about your order or how we can help make your life safer on the job?",
                                                        )}
                                                    />
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        );
                                    })}
                                </StyledWrapper>
                            </Tab>
                        </TabGroup>
                    </StyledForm>
                </StoreFormWrapper>
            )}
        </LoadingOverlay>
    );
};

const contentTab = {
    displayName: "Content",
    sortOrder: 1,
};

const validationTab = {
    displayName: "Validation",
    sortOrder: 2,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RequestAQuoteForm),
    definition: {
        group: "Basic",
        displayName: "Request A Quote Form",
        fieldDefinitions: [
            {
                name: fields.validateReCaptcha,
                displayName: "Validate reCaptcha.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
                isRequired: false,
            },
            {
                name: fields.title1,
                displayName: "Title for panel one",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Getting the best in workwear is as easy as 1, 2, 3, so tell us a bit about yourself.",
                tab: contentTab,
            },
            {
                name: fields.text1,
                displayName: "Text for panel one",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Our quick and easy process will get you the information you need, fast.",
                tab: contentTab,
            },
            {
                name: fields.title2,
                displayName: "Title for panel two",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Just a little more information – you know about our company now tell us about yours.",
                tab: contentTab,
            },
            {
                name: fields.text2,
                displayName: "Text for panel one",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
                tab: contentTab,
            },
            {
                name: fields.title3,
                displayName: "Title for panel three",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "And now for the fun part – it's time to pick out what's caught your eye.",
                tab: contentTab,
            },
            {
                name: fields.text3,
                displayName: "Text for panel one",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "SELECT UP TO 3 PRODUCTS. LIKE MORE THAN THREE? NO PROBLEM, JUST TELL YOUR REP.",
                tab: contentTab,
            },
            {
                ...availableCategoriesField,
                tab: contentTab,
            },
            {
                ...availableStatesField,
                tab: contentTab,
            },
            {
                name: fields.successMessage,
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue:
                    "That’s it! A Kishigo sales team member will circle back with you within 24 hours. Thank you for considering Kishigo.",
                tab: validationTab,
            },
        ],
    },
};

export default widgetModule;
